import React from 'react';

export const REPORT_TEXT_SEARCH_MIN_LENGTH = 3;

export interface ReportsFormState {
  searchType: string;
  primaryValue: string;
  secondaryType: string;
  secondaryValue: string;
  marketSegmentItems: { label: string; value: string }[];
  loadingMarketSegments: boolean;
}

export interface UseReportsFormReturn extends ReportsFormState {
  isSearchValid: boolean;
  setSearchType: (type: string) => void;
  setPrimaryValue: (val: string) => void;
  setSecondaryType: (type: string) => void;
  setSecondaryValue: (val: string) => void;
  loadMarketSegments: (fetcher: () => Promise<string[]>) => void;
  reset: () => void;
}

const INITIAL_STATE: ReportsFormState = {
  searchType: 'producerName',
  primaryValue: '',
  secondaryType: '',
  secondaryValue: '',
  marketSegmentItems: [],
  loadingMarketSegments: false,
};

function computeIsSearchValid(state: ReportsFormState): boolean {
  const { searchType, primaryValue, secondaryType, secondaryValue } = state;
  switch (searchType) {
    case 'producerName':
    case 'agentName':
      return primaryValue.trim().length >= REPORT_TEXT_SEARCH_MIN_LENGTH;
    case 'marketSegment':
    case 'salesOfficeName':
      return (
        primaryValue.trim().length > 0 &&
        secondaryType.trim().length > 0 &&
        secondaryValue.trim().length > 0
      );
    default:
      return false;
  }
}

export function useReportsForm(): UseReportsFormReturn {
  const [state, setState] = React.useState<ReportsFormState>(INITIAL_STATE);

  const setSearchType = React.useCallback((type: string) => {
    setState((prev) => {return {
      ...prev,
      searchType: type,
      primaryValue: '',
      secondaryType: '',
      secondaryValue: '',
    }});
  }, []);

  const setPrimaryValue = React.useCallback((val: string) => {
    setState((prev) => {
      const isMarketSegment = prev.searchType === 'marketSegment';
      return {
        ...prev,
        primaryValue: val,
        secondaryType: isMarketSegment ? '' : prev.secondaryType,
        secondaryValue: isMarketSegment ? '' : prev.secondaryValue,
      };
    });
  }, []);

  const setSecondaryType = React.useCallback((type: string) => {
    setState((prev) => {return {
      ...prev,
      secondaryType: type,
      secondaryValue: '',
    }});
  }, []);

  const setSecondaryValue = React.useCallback((val: string) => {
    setState((prev) => {return { ...prev, secondaryValue: val }});
  }, []);

  const loadMarketSegments = React.useCallback((fetcher: () => Promise<string[]>) => {
    setState((prev) => {return { ...prev, loadingMarketSegments: true }});
    fetcher()
      .then((segments) => {
        setState((prev) => {return {
          ...prev,
          marketSegmentItems: segments.map((s) => {return { label: s, value: s }}),
          loadingMarketSegments: false,
        }});
      })
      .catch(() => {
        setState((prev) => {return { ...prev, loadingMarketSegments: false }});
      });
  }, []);

  const reset = React.useCallback(() => {
    setState(INITIAL_STATE);
  }, []);

  return {
    ...state,
    isSearchValid: computeIsSearchValid(state),
    setSearchType,
    setPrimaryValue,
    setSecondaryType,
    setSecondaryValue,
    loadMarketSegments,
    reset,
  };
}
